import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, MapPin, FileText, Clock, CheckCircle, Calendar, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Pagină SEO pentru articole blog - identic cu LocationPage
 * URL: /blog/certificat-constatator-punct-de-lucru, etc.
 */

function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Încarcă date articol + conținut
  useEffect(() => {
    const loadArticleData = async () => {
      try {
        // 1. Încarcă metadata articole din JSON
        const articlesResponse = await fetch('/seo-data/blog-articles.json');
        const articlesData = await articlesResponse.json();

        const articleData = articlesData.articles.find(a => a.slug === slug);

        if (!articleData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setArticle(articleData);

        // 2. Încarcă conținut Markdown
        const contentResponse = await fetch(`/seo-data/blog/${slug}.md`);
        if (!contentResponse.ok) {
          throw new Error('Conținut nu există');
        }

        const markdown = await contentResponse.text();

        // Extrage conținut fără frontmatter
        const contentWithoutFrontmatter = markdown.replace(/^---[\s\S]*?---\n/, '');

        setContent(contentWithoutFrontmatter);
        setLoading(false);

      } catch (error) {
        console.error('Error loading article data:', error);
        setNotFound(true);
        setLoading(false);
      }
    };

    loadArticleData();
  }, [slug]);

  // SEO helper values
  // seoTitle (optional) = titlu complet pentru Google, fara sufix; altfel titlu + " | Constatatoare.ro"
  const pageTitle = article ? (article.seoTitle || `${article.title} | Constatatoare.ro`) : 'Blog | Constatatoare.ro';
  const dateModified = article ? (article.updatedAt || article.publishedAt) : null;

  // FAQ din sectiunea "## Întrebări frecvente" a articolului (format: **Întrebare?** pe un rând, răspuns pe rândul următor)
  const faqItems = (() => {
    const start = content.indexOf('## Întrebări frecvente');
    if (start === -1) return [];
    const rest = content.slice(start + 3);
    const end = rest.indexOf('\n## ');
    const section = end === -1 ? rest : rest.slice(0, end);
    return [...section.matchAll(/\*\*(.+?\?)\*\*\n(.+)/g)].map(m => ({
      question: m[1].trim(),
      answer: m[2].replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\*\*/g, '').trim()
    }));
  })();
  const metaDescription = article ? article.description : '';
  const canonicalUrl = `https://constatatoare.ro/blog/${slug}/`;
  const ogImageUrl = 'https://constatatoare.ro/og-image-v2.jpg';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Articolul nu a fost găsit
          </h1>
          <p className="text-gray-600 mb-8">
            Pagina pentru acest articol nu există încă.
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Înapoi la comandă
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
      {/* SEO Meta Tags cu React Helmet */}
      {article && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />

          {/* Open Graph / Facebook / WhatsApp */}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:image" content={ogImageUrl} />
          <meta property="og:locale" content="ro_RO" />
          <meta property="og:site_name" content="Constatatoare.ro" />
          <meta property="article:published_time" content={article.publishedAt} />
          <meta property="article:modified_time" content={dateModified} />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={ogImageUrl} />

          {/* Article + BreadcrumbList JSON-LD */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": article.title,
                "description": article.description,
                "url": canonicalUrl,
                "datePublished": article.publishedAt,
                "dateModified": dateModified,
                "author": {
                  "@type": "Organization",
                  "@id": "https://constatatoare.ro/#organization",
                  "name": "Constatatoare.ro",
                  "url": "https://constatatoare.ro/"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Constatatoare.ro",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://constatatoare.ro/logo-full.png"
                  }
                },
                "image": ogImageUrl,
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": canonicalUrl
                },
                "inLanguage": "ro-RO",
                "keywords": article.keywords ? article.keywords.join(", ") : ""
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Acasă",
                    "item": "https://constatatoare.ro/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Blog",
                    "item": "https://constatatoare.ro/blog/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": article.title,
                    "item": canonicalUrl
                  }
                ]
              }
            ]
          })}</script>

          {/* FAQPage JSON-LD - doar daca articolul are sectiunea "Întrebări frecvente" */}
          {faqItems.length > 0 && (
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqItems.map(f => ({
                "@type": "Question",
                "name": f.question,
                "acceptedAnswer": { "@type": "Answer", "text": f.answer }
              }))
            })}</script>
          )}
        </Helmet>
      )}

      <Header />

      {/* Hero Section cu breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-emerald-600 transition">
                  Acasă
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/blog/" className="hover:text-emerald-600 transition">
                  Blog
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{article.title}</li>
            </ol>
          </nav>

          {/* Title cu icon */}
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-8 w-8 text-emerald-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              {article.title}
            </h1>
          </div>

          {/* Subtitle cu date articol */}
          <p className="text-lg text-gray-600 flex items-center flex-wrap gap-4">
            <span className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-emerald-600" />
              {article.updatedAt && 'Actualizat: '}
              {new Date(dateModified).toLocaleDateString('ro-RO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="text-gray-400">•</span>
            <span className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-emerald-600" />
              {article.readTime} min citire
            </span>
            <span className="text-gray-400">•</span>
            <span className="flex items-center">
              <Tag className="h-5 w-5 mr-2 text-emerald-600" />
              {article.category}
            </span>
          </p>
        </div>
      </div>

      {/* Main Content - Layout 2 coloane */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-[1fr_350px] gap-8">
          {/* Coloană stângă - Conținut articol */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* CTA top - înainte de articol */}
            <div className="mb-8 p-6 bg-emerald-50 rounded-xl border border-emerald-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-2" />
                Comandă acum certificatul tău
              </h3>
              <p className="text-gray-700 mb-4">
                Procesul durează doar 5 minute. Livrare instant prin email, fără cont.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition shadow-lg"
              >
                Comandă certificat constatator
              </button>
            </div>

            {/* Conținut markdown */}
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              <ReactMarkdown
                components={{
                  h2: (props) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{props.children}</h2>,
                  h3: (props) => <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">{props.children}</h3>,
                  p: (props) => <p className="mb-4">{props.children}</p>,
                  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2">{props.children}</ul>,
                  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2">{props.children}</ol>,
                  li: (props) => <li className="text-gray-700">{props.children}</li>,
                  strong: (props) => <strong className="font-bold text-gray-900">{props.children}</strong>,
                  a: ({ href, children }) => {
                    const isExternal = href && /^https?:\/\//.test(href) && !href.includes('constatatoare.ro');
                    return (
                      <a
                        href={href}
                        className="text-emerald-600 font-medium underline decoration-emerald-300 underline-offset-2 hover:text-emerald-700 hover:decoration-emerald-600"
                        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {children}
                      </a>
                    );
                  },
                  table: (props) => <table className="w-full border-collapse mb-4">{props.children}</table>,
                  th: (props) => <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left font-bold">{props.children}</th>,
                  td: (props) => <td className="border border-gray-300 px-4 py-2">{props.children}</td>,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            {/* CTA bottom - după articol */}
            <div className="mt-8 p-6 bg-gradient-to-r from-emerald-600 to-cyan-600 rounded-xl text-white">
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <CheckCircle className="h-6 w-6 mr-2" />
                Ai nevoie de certificat constatator?
              </h3>
              <p className="mb-4 text-emerald-100">
                Comandă online în 5 minute. Document oficial ONRC, livrare pe email.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-8 py-3 bg-white text-emerald-600 font-bold rounded-lg hover:bg-gray-100 transition shadow-lg"
              >
                Comandă acum - 99 lei
              </button>
            </div>
          </div>

          {/* Coloană dreapta - Sidebar cu features */}
          <div className="space-y-6">
            {/* Card avantaje */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                De ce să comanzi online?
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: Clock, text: 'Procesare în 5 minute' },
                  { icon: FileText, text: 'Document oficial ONRC' },
                  { icon: MapPin, text: 'Fără portaluri greoaie' },
                  { icon: CheckCircle, text: 'Livrare gratuită prin email' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <item.icon className="h-5 w-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card articole conexe */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Articole similare
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Alte ghiduri utile despre certificatul constatator:
              </p>
              <div className="space-y-3">
                {article.relatedArticles && article.relatedArticles.map((related, index) => (
                  <Link
                    key={index}
                    to={`/blog/${related.slug}/`}
                    className="block p-3 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium border border-emerald-200 hover:bg-emerald-100 transition"
                  >
                    → {related.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default BlogArticlePage;
